import React, { useEffect, useState } from 'react';
import { connect, DispatchProp } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { CCard, CCardHeader, CCardBody, CRow, CCol, CCardTitle, CLabel, CFormGroup, CButton } from '@coreui/react';
import { RootState } from '../../store/state';
import { JourneyConfig } from '../../shared/models/journey';
import Select from '../../shared/components/form/select/Select';

const vehicleImages = {
  van: 'https://viewer.content.glympse.com/apps/enroute/journey/journey3/1.23.0/images/agent/unbrandedVan.svg',
  truck: 'https://p-web-view-service.s3.amazonaws.com/configs/partners/cb/journey3/stable/images/unbrandedTruck.svg',
};

const DEFAULT_GLYMPSE_LOGO = 'https://viewer.content.glympse.com/apps/tickets/default/1.4.15/images/favicon-32x32.png';

export enum VehicleImageType {
  Van = 'van',
  Truck = 'truck',
  Custom = 'custom'
};

const vehicleOptions = [
  {
    value: VehicleImageType.Van,
    label: 'Van',
    isFixed: true,
  },
  {
    value: VehicleImageType.Truck,
    label: 'Truck',
    isFixed: true,
  },
  {
    value: VehicleImageType.Custom,
    label: 'Custom',
    isFixed: true,
  },
];

export interface VehicleIconFormProps extends DispatchProp {
  orgID: number;
  config: JourneyConfig;
  handleChangeVehicleIcon: (vehicleIcon: File, logoIcon: File, vehicleLogoPreviewImage: string, vehicleType: VehicleImageType) => void;
}

const VehicleIconForm = (props: VehicleIconFormProps) => {
  const { config, handleChangeVehicleIcon } = props;

  const [vehicleType, setVehicleType] = useState(config?.theme?.agentIcon?.vehicleType);
  const [vehiclePreviewImage, setVehiclePreviewImage] = useState(config?.theme?.agentIcon?.vehicle?.[config?.theme?.agentIcon?.vehicleType]);
  const [vehicleLogoPreviewImage, setVehicleLogoPreviewImage] = useState(config?.theme?.vehicleBrandingIcon);

  const handleVehicleImageChange = files => {
    const objectUrl = URL.createObjectURL(files[0]);
    const file = files[0];
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onloadend = () => {
      setVehiclePreviewImage(objectUrl);
    };
  };

  const handleVehicleLogoChange = files => {
    const objectUrl = URL.createObjectURL(files[0]);
    const file = files[0];
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onloadend = () => {
      setVehicleLogoPreviewImage(objectUrl);
    };
  };

  const vehicleDropzone = useDropzone({
    accept: 'image/jpeg, image/png',
    maxFiles: 1,
    onDropAccepted: handleVehicleImageChange,
  });

  const logoDropzone = useDropzone({
    accept: 'image/jpeg, image/png',
    maxFiles: 1,
    onDropAccepted: handleVehicleLogoChange,
  });

  const changeVehicleLogoUrl = (url) => {
    // @ts-ignore
    logoDropzone?.acceptedFiles?.length = 0;
    setVehicleLogoPreviewImage(url);
  };

  const handleVehicleTypeChange = (data) => {
    setVehicleType(data);
    setVehiclePreviewImage(vehicleImages[data] || config?.theme?.agentIcon?.vehicle?.custom);
  };

  useEffect(() => {
    if (config?.theme?.agentIcon?.vehicleType && config?.theme?.agentIcon?.vehicle?.[config?.theme?.agentIcon?.vehicleType]) {
      setVehiclePreviewImage(config?.theme?.agentIcon?.vehicle?.[config?.theme?.agentIcon?.vehicleType]);
    }
    if (config?.theme?.agentIcon?.vehicleType) {
      setVehicleType(config?.theme?.agentIcon?.vehicleType);
    }
    if (config?.theme?.vehicleBrandingIcon) {
      setVehicleLogoPreviewImage(config?.theme?.vehicleBrandingIcon);
    }
  }, [config?.theme]);

  useEffect(() => {
    handleChangeVehicleIcon(vehicleDropzone?.acceptedFiles?.[0], logoDropzone?.acceptedFiles?.[0], vehicleLogoPreviewImage, vehicleType);
  }, [vehicleDropzone?.acceptedFiles?.[0], logoDropzone?.acceptedFiles?.[0], vehicleLogoPreviewImage, vehicleType]);

  let vehicleDropzoneMessage = vehicleDropzone.isDragActive ? 'Drop the file here' : 'Drag and drop a file here, or click to select a file';
  if (vehicleDropzone.acceptedFiles.length > 0) {
    vehicleDropzoneMessage = `Current File: ${vehicleDropzone.acceptedFiles[0].name}`;
  }

  let logoDropzoneMessage = logoDropzone.isDragActive ? 'Drop the file here' : 'Drag and drop a file here, or click to select a file';
  if (logoDropzone.acceptedFiles.length > 0) {
    logoDropzoneMessage = `Current File: ${logoDropzone.acceptedFiles[0].name}`;
  }

  return (
    <CCard className="glympser-container--roundedWithShadow">
      <CCardHeader className="glympser-roundedTop">
        <CCardTitle className="m-0">
          Vehicle Image
        </CCardTitle>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol sm={6}>
            <CRow>
              <CCol lg={12}>
                <CFormGroup>
                  <CLabel htmlFor="vehicle">Vehicle Type</CLabel>
                  <Select
                    isMulti={false}
                    isClearable={false}
                    name="vehicle-type"
                    id="vehicle"
                    options={vehicleOptions}
                    classNamePrefix="select"
                    value={vehicleType}
                    onChange={handleVehicleTypeChange}
                  />
                </CFormGroup>
              </CCol>
              <CCol lg="12" className="d-flex align-items-center justify-content-center">
                {vehiclePreviewImage &&
                  <img alt="vehicle preview" className="vehicle-image-preview mb-3" src={vehiclePreviewImage}
                       key={vehiclePreviewImage} />}
              </CCol>
              <CCol lg="12">
                {
                  vehicleType === VehicleImageType.Custom ? (
                    <>
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <div {...vehicleDropzone.getRootProps({ className: 'dropzone' })}>
                        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                        <input {...vehicleDropzone.getInputProps() as any} />
                        <p className="w-full mb-0">{vehicleDropzoneMessage}</p>
                        <em>(Only *.jpeg and *.png images will be accepted)</em>
                      </div>
                      <p className="mt-2 mb-0 text-center">Your image will be scaled to fit within a space of 40 pixels
                        wide by 80 pixels tall. Image must be top view of the vehicle on a transparent background with
                        the &quot;front&quot; pointing up.</p>
                    </>
                  ) : null
                }
              </CCol>
            </CRow>
          </CCol>
          <CCol sm={6}>
            <CRow>
              <CCol lg="12" className="d-flex align-items-center justify-content-center flex-column">
                <p>Vehicle Logo</p>
                {vehicleLogoPreviewImage && <img alt="vehicle logo preview" className="vehicle-logo-image-preview mb-3"
                                                 src={vehicleLogoPreviewImage} key={vehicleLogoPreviewImage} />}
              </CCol>
              <CCol lg="12">
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <div {...logoDropzone.getRootProps({ className: 'dropzone' })}>
                  {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                  <input {...logoDropzone.getInputProps() as any} />
                  <p className="w-full mb-0">{logoDropzoneMessage}</p>
                  <em>(Only *.jpeg and *.png images will be accepted)</em>
                </div>
                <p className="mt-2 mb-0 text-center">Your image will be scaled to fit within a space of 25 pixels wide
                  by 25 pixels tall. Image must have transparent background.</p>
                <CButton className="mt-3 bg-color-orange roundedBtn" block color="info"
                         onClick={() => changeVehicleLogoUrl(null)} disabled={!vehicleLogoPreviewImage}>Remove Vehicle
                  Logo</CButton>
                <CButton className="mt-3 bg-color-orange roundedBtn" block color="info"
                         onClick={() => changeVehicleLogoUrl(DEFAULT_GLYMPSE_LOGO)}>Revert to Default Logo</CButton>
              </CCol>
            </CRow>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  orgID: state?.orgHierarchy?.selectedOrg?._id,
  config: state?.journey?.config?.data,
});

export default connect(mapStateToProps)(VehicleIconForm);
